import React from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import { ChevronDown, ChevronUp } from 'lucide-react';

const Table = ({ 
  columns, 
  data, 
  onRowClick,
  sortable = false,
  sortColumn,
  sortDirection,
  onSort,
  emptyMessage = 'No data available',
  loading = false 
}) => {
  const { theme } = useTheme();

  const wrapperStyles = {
    overflowX: 'auto',
    border: `1px solid ${theme.colors.border}`,
    borderRadius: '10px',
    backgroundColor: theme.colors.cardBg,
  };

  const tableStyles = {
    width: '100%',
    borderCollapse: 'separate',
    borderSpacing: 0,
  };

  const theadStyles = {
    backgroundColor: 'transparent',
  };

  const thStyles = {
    padding: '12px 16px',
    textAlign: 'left',
    fontSize: '12px',
    fontWeight: '600',
    color: theme.colors.textSecondary,
    textTransform: 'none',
    letterSpacing: '0.02em',
    whiteSpace: 'nowrap',
    borderBottom: `1px solid ${theme.colors.border}`,
  };

  const sortableThStyles = {
    ...thStyles,
    cursor: 'pointer',
    userSelect: 'none',
    position: 'relative',
  };

  const tbodyStyles = {};

  const trStyles = {
    transition: 'background-color 0.15s ease',
  };

  const getTdStyles = (isLastRow) => ({
    padding: '14px 16px',
    fontSize: '14px',
    color: theme.colors.text,
    borderBottom: isLastRow ? 'none' : `1px solid ${theme.colors.border}`,
  });

  const emptyStyles = {
    textAlign: 'center',
    padding: '48px 16px',
    color: theme.colors.textSecondary,
    fontSize: '14px',
  };

  const loadingStyles = {
    textAlign: 'center',
    padding: '48px 16px',
    color: theme.colors.textSecondary,
  };

  const handleSort = (column) => {
    if (!sortable || !column.sortable || !onSort) return;
    onSort(column.key);
  };

  const getSortIcon = (columnKey) => {
    if (sortColumn !== columnKey) return null;
    return sortDirection === 'asc' ? <ChevronUp size={14} /> : <ChevronDown size={14} />;
  };

  if (loading) {
    return (
      <div style={loadingStyles}>
        <div className="spinner" style={{
          border: `3px solid ${theme.colors.border}`,
          borderTop: `3px solid ${theme.colors.primary}`,
          borderRadius: '50%',
          width: '40px',
          height: '40px',
          animation: 'spin 1s linear infinite',
          margin: '0 auto 16px',
        }}></div>
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        <p>Loading...</p>
      </div>
    );
  }

  return (
    <div style={wrapperStyles}>
      <table style={tableStyles}>
        <thead style={theadStyles}>
          <tr>
            {columns.map((column) => (
              <th
                key={column.key}
                style={column.sortable && sortable ? sortableThStyles : thStyles}
                onClick={() => handleSort(column)}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  {column.label}
                  {column.sortable && getSortIcon(column.key)}
                </div>
              </th>
            ))}
          </tr>
        </thead>
        <tbody style={tbodyStyles}>
          {data.length === 0 ? (
            <tr>
              <td colSpan={columns.length} style={emptyStyles}>
                {emptyMessage}
              </td>
            </tr>
          ) : (
            data.map((row, index) => (
              <tr
                key={row.id || index}
                style={trStyles}
                onClick={() => onRowClick && onRowClick(row)}
                onMouseEnter={(e) => {
                  if (onRowClick) {
                    e.currentTarget.style.backgroundColor = theme.colors.backgroundSecondary;
                    e.currentTarget.style.cursor = 'pointer';
                  }
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent';
                }}
              >
                {columns.map((column) => (
                  <td key={column.key} style={getTdStyles(index === data.length - 1)}>
                    {column.render ? column.render(row) : row[column.key]}
                  </td>
                ))}
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
};

export default Table;
